import { useCallback, useEffect, useMemo, useRef } from 'react';

import { DASHBOARD_URL, HOWDENS_URL } from 'config';
import { useStatusContext } from 'pages/Status/context';

import {
  INTRODUCTION_TITLE,
  LINE_2_STANDARD,
  LINE_2_SUPPLY,
  REDIRECT_SUBTITLE,
  REDIRECT_TITLE,
  SUBTITLE_STANDARD,
  SUBTITLE_SUPPLY,
  TITLE,
} from './constants';

interface ReadyToStartHook {
  title: string;
  subtitle: string[];
  line2: string;
  companyName: string;
  isExampleJob: boolean;
  exampleReturnUrl: string;
  successUrl?: string;
  isLoading: boolean;
}

export default function useReadyToStart(): ReadyToStartHook {
  const {
    job,
    jobCompanyInfo,
    isLoading,
    isIntroduced,
    isExampleJob,
    isDeliveryOnly,
  } = useStatusContext();

  const successUrl = useMemo(() => job?.redirect_urls?.success_url, [job]);

  const companyName = useMemo(() => {
    if (!jobCompanyInfo || isLoading) return 'Your tradesperson';
    return jobCompanyInfo?.company_name || 'Your tradesperson';
  }, [jobCompanyInfo, isLoading]);

  const subtitle = useMemo(() => {
    if (successUrl)
      return REDIRECT_SUBTITLE.map((part) =>
        part.replace('$partner', companyName),
      );
    const base = isDeliveryOnly ? SUBTITLE_SUPPLY : SUBTITLE_STANDARD;
    return base.map((part) => part.replace('$partner', companyName));
  }, [isDeliveryOnly, successUrl, companyName]);

  const title = useMemo(() => {
    if (isIntroduced) return INTRODUCTION_TITLE;
    return successUrl ? REDIRECT_TITLE : TITLE;
  }, [isIntroduced, successUrl]);

  const line2 = useMemo(
    () => (isDeliveryOnly ? LINE_2_SUPPLY : LINE_2_STANDARD),
    [isDeliveryOnly],
  );

  const jobId = useMemo(() => job?.id, [job]);

  const timerRef = useRef<NodeJS.Timer | undefined>();

  const startTimeout = useCallback(() => {
    clearTimeout(timerRef.current as NodeJS.Timeout);
    timerRef.current = setTimeout(() => {
      window.location.assign(successUrl as string);
    }, 10000);
  }, [successUrl]);

  const exampleReturnUrl = useMemo(() => {
    if (!job?.introduced_to) return `${DASHBOARD_URL}/job-details/${jobId}`;
    return `${HOWDENS_URL}/job/${jobId}`;
  }, [jobId, job]);

  useEffect(() => {
    if (successUrl) startTimeout();
    return () => clearTimeout(timerRef.current as NodeJS.Timeout);
  }, [successUrl, startTimeout]);

  return {
    title,
    subtitle,
    line2,
    companyName,
    successUrl,
    isExampleJob,
    exampleReturnUrl,
    isLoading,
  };
}
